import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfigurationService, UserListDto } from '../configuration.service';
import { AuthService } from '../../services/auth.service';
import { UserCreateDialogComponent } from './user-create-dialog.component';
import { UserInviteDialogComponent } from './user-invite-dialog.component';
import { UserDetailComponent, UserDetailDialogData } from './user-detail.component';

type SortField = 'userName' | 'email' | 'status';

@Component({
  selector: 'app-configuration-users',
  templateUrl: './users.component.html',
  styleUrls: ['../configuration-list.css'],
})
export class ConfigurationUsersComponent implements OnInit {
  users: UserListDto[] = [];
  loading = false;
  search = '';
  sortField: SortField | null = null;
  sortDir: 'asc' | 'desc' = 'asc';

  constructor(
    private api: ConfigurationService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    public auth: AuthService
  ) {}

  ngOnInit(): void { this.load(); }

  load(): void {
    // Only spin on the first load so a post-save refresh doesn't flash the table.
    if (!this.users.length) this.loading = true;
    this.api.getUsers().subscribe({
      next: rows => { this.users = rows ?? []; this.loading = false; },
      error: () => {
        this.loading = false;
        this.snackBar.open('Failed to load users', 'Retry', { duration: 5000 })
          .onAction().subscribe(() => this.load());
      }
    });
  }

  trackById(_: number, u: UserListDto): number { return u.id; }

  get filtered(): UserListDto[] {
    const q = this.search.trim().toLowerCase();
    let rows = q
      ? this.users.filter(u =>
          (u.userName ?? '').toLowerCase().includes(q)
          || u.emailAddress.toLowerCase().includes(q)
          || u.roles.some(r => r.toLowerCase().includes(q)))
      : this.users;

    if (this.sortField) {
      const field = this.sortField;
      const dir = this.sortDir === 'asc' ? 1 : -1;
      rows = [...rows].sort((a, b) =>
        this.sortValue(a, field).localeCompare(this.sortValue(b, field)) * dir);
    }
    return rows;
  }

  private sortValue(u: UserListDto, field: SortField): string {
    switch (field) {
      case 'userName': return (u.userName ?? u.emailAddress).toLowerCase();
      case 'email':    return u.emailAddress.toLowerCase();
      case 'status':   return u.canLogin ? 'active' : 'disabled';
    }
  }

  sortBy(field: SortField): void {
    if (this.sortField === field) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDir = 'asc';
    }
  }

  visibleRoles(roles: string[]): string[] {
    return roles.slice(0, 3);
  }

  extraRoleCount(roles: string[]): number {
    return Math.max(0, roles.length - 3);
  }

  openAddUser(): void {
    const ref = this.dialog.open(UserCreateDialogComponent, { width: '520px', maxWidth: '92vw' });
    ref.afterClosed().subscribe(result => { if (result?.created) this.load(); });
  }

  openInviteUser(): void {
    const ref = this.dialog.open(UserInviteDialogComponent, { width: '440px', maxWidth: '92vw' });
    ref.afterClosed().subscribe(result => { if (result?.invited) this.load(); });
  }

  goToDetail(user: UserListDto): void {
    const ref = this.dialog.open<UserDetailComponent, UserDetailDialogData>(
      UserDetailComponent,
      { width: '560px', maxWidth: '92vw', data: { user } }
    );
    ref.afterClosed().subscribe(() => this.load());
  }

  // Soft delete: reversible enable/disable via the update endpoint.
  toggleActive(user: UserListDto): void {
    const enable = !user.canLogin;
    const verb = enable ? 'Reactivate' : 'Deactivate';
    if (!confirm(`${verb} ${user.userName || user.emailAddress}?`)) return;
    this.api.updateUser(user.id, {
      emailAddress: user.emailAddress,
      userName:     user.userName,
      canLogin:     enable,
    }).subscribe({
      next: () => this.load(),
      error: () => this.snackBar.open(`Failed to ${verb.toLowerCase()} user`, undefined, { duration: 4000 }),
    });
  }

  // Hard delete: removes the row. Backend returns 409 if the user is still
  // referenced (then deactivation is the only option).
  deleteUser(user: UserListDto): void {
    if (!confirm(`Permanently delete ${user.userName || user.emailAddress}? This cannot be undone.`)) return;
    this.api.deleteUser(user.id).subscribe({
      next: () => { this.snackBar.open('User deleted', undefined, { duration: 3000 }); this.load(); },
      error: err => {
        const msg = err.status === 409
          ? (err.error?.message || "This user can't be deleted; deactivate instead.")
          : `Failed to delete user (error ${err.status || 'network'}).`;
        this.snackBar.open(msg, undefined, { duration: 6000 });
      },
    });
  }
}
